import { useGameStore } from '@store/gameStore'
import { LandingPage } from '@screens/LandingPage'
import { SmsIntroScreen } from '@screens/SmsIntroScreen'
import { HomeScreenScreen } from '@screens/HomeScreenScreen'
import { SetupScreen } from '@screens/SetupScreen'
import { LoadingScreen } from '@screens/LoadingScreen'
import { TutorialScreen } from '@screens/TutorialScreen'
import { CityScreen } from '@screens/CityScreen'
import { ShopScreen } from '@screens/ShopScreen'
import { RankingScreen } from '@screens/RankingScreen'
import { EndScreen } from '@screens/EndScreen'
import { HeroScreen } from '@screens/HeroScreen'
import { GachaScreen } from '@screens/GachaScreen'
import { DailyScreen } from '@screens/DailyScreen'
import { BattleScreen } from '@screens/BattleScreen'
import { BattlePassScreen } from '@screens/BattlePassScreen'
import { BankScreen } from '@screens/BankScreen'
import { useEffect, useState } from 'react'

import { Preloader } from '@components/Preloader'
import { TutorialOverlay } from '@components/TutorialOverlay'
import { TutorialSkipModal } from '@components/TutorialSkipModal'
import { TutorialFinale } from '@components/TutorialFinale'

function App() {
    const currentScreen = useGameStore(state => state.currentScreen)
    const passiveTick = useGameStore(state => state.passiveTick)
    const tutorialPhase = useGameStore(state => state.tutorialPhase)
    const setTutorialPhase = useGameStore(state => state.setTutorialPhase)
    const skipTutorial = useGameStore(state => state.skipTutorial)


    // Global Game Loop
    useEffect(() => {
        const interval = setInterval(() => {
            passiveTick()
        }, 1000)
        return () => clearInterval(interval)
    }, [passiveTick])

    // PWA Logic
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null)
    const [showPwaModal, setShowPwaModal] = useState(false)

    useEffect(() => {
        const handler = (e: any) => {
            e.preventDefault()
            setDeferredPrompt(e)
            setShowPwaModal(true)
        }
        window.addEventListener('beforeinstallprompt', handler)
        return () => window.removeEventListener('beforeinstallprompt', handler)
    }, [])

    const handleInstall = async () => {
        if (!deferredPrompt) return
        deferredPrompt.prompt()
        const choice = await deferredPrompt.userChoice
        if (choice.outcome === 'accepted') {
            setShowPwaModal(false)
        }
        setDeferredPrompt(null)
    }

    const handleDismissPwa = () => {
        setShowPwaModal(false)
    }

    // Screen Router
    const renderScreen = () => {
        switch (currentScreen) {
            case 'landing':
                return <LandingPage />
            case 'smsintro':
                return <SmsIntroScreen />
            case 'homescreen':
                return <HomeScreenScreen />
            case 'setup':
                return <SetupScreen />
            case 'loading':
                return <LoadingScreen />
            case 'tutorial':
                return <TutorialScreen />
            case 'city':
                return <CityScreen />
            case 'shop':
                return <ShopScreen />
            case 'ranking':
                return <RankingScreen />
            case 'end':
                return <EndScreen />
            case 'hero':
                return <HeroScreen />
            case 'gacha':
                return <GachaScreen />
            case 'daily':
                return <DailyScreen />
            case 'battle':
                return <BattleScreen />
            case 'battlepass':
                return <BattlePassScreen />
            case 'bank':
                return <BankScreen />
            // Fallback for screens not yet implemented
            case 'vip':
            case 'event':
            case 'referral':
            case 'leaderboard':
                return <CityScreen /> // Redirect to city for now
            default:
                return <LandingPage />
        }
    }

    return (
        <div className="app">
            <Preloader />
            {renderScreen()}

            {/* Global PWA Modal */}
            {showPwaModal && (
                <div className="pwa-modal-overlay">
                    <div className="pwa-modal">
                        <img src="/appicon.png" alt="App Icon" className="pwa-icon" />
                        <h3 className="pwa-title">Besser als App! 🚀</h3>
                        <p className="pwa-desc">
                            Installiere <strong>Pay2Lose</strong> für Vollbild-Modus, bessere Performance und das echte Premium-Feeling.
                        </p>
                        <div className="pwa-actions">
                            <button className="pwa-btn-install" onClick={handleInstall}>
                                📲 JETZT INSTALLIEREN
                            </button>
                            <button className="pwa-btn-skip" onClick={handleDismissPwa}>
                                Vielleicht später
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tutorial Overlay (global highlights) */}
            <TutorialOverlay />

            {/* Tutorial Skip Choice (shown when tutorial starts) */}
            {tutorialPhase === 'not-started' && currentScreen === 'tutorial' && (
                <TutorialSkipModal
                    onStart={() => setTutorialPhase('building')}
                    onSkip={skipTutorial}
                />
            )}

            {/* Tutorial Finale */}
            {tutorialPhase === 'finale' && <TutorialFinale />}
        </div>
    )
}

export default App
